/**
 * Marketing banner admin types (REW-1092).
 * Mirrors rewards-service app/schemas/v1/marketing_banner*.py.
 */

export type MarketingBannerStatus = 'ACTIVE' | 'INACTIVE';
export type MarketingBannerSourceType = 'static' | 'external_api';
export type MarketingBannerCtaAction = 'external_browser' | 'rewards_route' | 'cross_mfe' | 'start_redemption';
export type MarketingMessageAnimation = 'none' | 'typewriter';

export interface MarketingMessage {
  id: string;
  text: string;
  animation?: MarketingMessageAnimation;
}

export interface MarketingBannerStaticContent {
  title?: string | null;
  image_name?: string | null;
  cta_label?: string | null;
  cta_action?: MarketingBannerCtaAction | null;
  cta_target?: string | null;
  cta_host?: string | null;
  cta_metadata_key?: string | null;
  cta_reward_key?: string | null;
  message_animation?: MarketingMessageAnimation | null;
  messages?: MarketingMessage[];
}

export interface ProviderReadApiConfig {
  base_url?: Record<string, string> | null;
  endpoint: string;
  method?: string;
  headers?: Record<string, string>;
  payload?: Record<string, unknown>;
}

export interface MarketingBannerRecord {
  id: string;
  name: string;
  priority: number;
  status: MarketingBannerStatus;
  source_type: MarketingBannerSourceType;
  static_content?: MarketingBannerStaticContent | null;
  external_api?: ProviderReadApiConfig | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface MarketingBannerQueryParams {
  status?: MarketingBannerStatus | null;
  search?: string | null;
  limit?: number;
  offset?: number;
}

export interface MarketingBannerListResponse {
  items: MarketingBannerRecord[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export type CreateMarketingBannerRequest = {
  name: string;
  priority: number;
  status: MarketingBannerStatus;
  source_type: MarketingBannerSourceType;
  static_content?: MarketingBannerStaticContent | null;
  external_api?: ProviderReadApiConfig | null;
};

export type UpdateMarketingBannerRequest = CreateMarketingBannerRequest;

export const MARKETING_BANNER_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
] as const;

export const MARKETING_BANNER_SOURCE_OPTIONS = [
  { value: 'external_api', label: 'External API (BFF upstream)' },
  { value: 'static', label: 'Static (no upstream)' },
] as const;

export const MARKETING_BANNER_CTA_ACTION_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'external_browser', label: 'Open in external browser' },
  { value: 'rewards_route', label: 'Rewards in-app route' },
  { value: 'cross_mfe', label: 'Cross-MFE (federated module)' },
  { value: 'start_redemption', label: 'Start in-app redemption (reward catalog)' },
] as const;

export const MARKETING_MESSAGE_ANIMATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'typewriter', label: 'Typewriter' },
] as const;

/** Blank create form — operators configure source type and content explicitly. */
export const BLANK_MARKETING_BANNER: CreateMarketingBannerRequest = {
  name: '',
  priority: 1,
  status: 'INACTIVE',
  source_type: 'static',
  static_content: {
    title: '',
    image_name: '',
    cta_label: '',
    message_animation: 'none',
    messages: [{ id: 'msg-1', text: '', animation: 'none' }],
  },
};
